const pickFirstString = (...values) => {
  for (const value of values) {
    const text = typeof value === 'string' ? value.trim() : '';
    if (text) return text;
  }
  return '';
};

const buildAvatarStyle = ({ root, file, buildAssetUrl, appendVariant }) => {
  const url = appendVariant(buildAssetUrl(root, file), 'medium');
  return url
    ? {
        backgroundImage: `url(${url})`,
        backgroundSize: 'cover',
        backgroundPosition: 'center',
      }
    : undefined;
};

const normalizeArtistGender = (gender) => {
  const value = String(gender || '').trim().toLowerCase();
  if (['male', 'man', 'm', '男性', '男'].includes(value)) return 'male';
  if (['female', 'woman', 'f', '女性', '女'].includes(value)) return 'female';
  return 'unknown';
};

export const getGroupArtistGender = (artist) =>
  pickFirstString(artist?.gender, artist?.artist_gender, artist?.artistGender);

// アバターのfallback（イニシャル表示）は背景色に加えて必ず白文字を返す。
// 文字色を style に同梱することで、.side-avatar 等の暗い既定色(slate-600)を
// 各所で上書きし、正準の PersonAvatar（color: --color-inverse）と一致させる。
export const getArtistFallbackAvatarStyle = (gender) => {
  const color = 'var(--color-inverse)';
  switch (normalizeArtistGender(gender)) {
    case 'male':
      return { backgroundColor: 'var(--person-avatar-artist-male-bg)', color };
    case 'female':
      return { backgroundColor: 'var(--person-avatar-artist-female-bg)', color };
    default:
      return { backgroundColor: 'var(--person-avatar-artist-unknown-bg)', color };
  }
};

export const getGroupAssigneeName = (admin) =>
  pickFirstString(admin?.full_name, admin?.username, admin?.display_name) || '管理者';

export const getGroupAssigneeMeta = (admin, fallback = '') =>
  pickFirstString(admin?.position, admin?.department) || fallback;

export const getGroupArtistName = (artist, fallback = 'アーティスト') =>
  pickFirstString(artist?.name, artist?.name_en, artist?.display_name) || fallback;

export const getGroupArtistPictureKey = (artist) =>
  pickFirstString(artist?.profile_picture, artist?.artist_profile_picture, artist?.pic);

export const getGroupAdminPictureKey = (admin) =>
  pickFirstString(admin?.profile_picture, admin?.admin_profile_picture, admin?.picture, admin?.pic);

export const getGroupAssigneeAvatarStyle = ({
  admin,
  buildAssetUrl,
  appendVariant,
  adminProfilePath,
}) =>
  buildAvatarStyle({
    root: adminProfilePath,
    file: getGroupAdminPictureKey(admin),
    buildAssetUrl,
    appendVariant,
  });

export const getGroupArtistAvatarStyle = ({
  artist,
  buildAssetUrl,
  appendVariant,
  artistProfilePath,
}) => {
  const imageStyle = buildAvatarStyle({
    root: artistProfilePath,
    file: getGroupArtistPictureKey(artist),
    buildAssetUrl,
    appendVariant,
  });
  return imageStyle || getArtistFallbackAvatarStyle(getGroupArtistGender(artist));
};

export const getGroupAssigneeAvatarFallback = (admin, fallback = '管理者') => {
  if (getGroupAdminPictureKey(admin)) return '';
  return getGroupAssigneeName(admin).charAt(0) || fallback.charAt(0) || 'A';
};

export const getGroupArtistAvatarFallback = (artist, fallback = 'アーティスト') => {
  if (getGroupArtistPictureKey(artist)) return '';
  return getGroupArtistName(artist, fallback).charAt(0) || fallback.charAt(0) || '?';
};

export const resolveGroupAssigneeKey = (admin, index) =>
  admin?.id || admin?.admin_user_id || admin?.username || `admin-${index}`;

export const resolveGroupArtistKey = (artist, index, group, prefix = 'artist') =>
  String(artist?.artist_id || artist?.id || '').trim() ||
  `${prefix}-${String(group?.key || group?.id || 'group')}-${index}`;

export function createGroupParticipantsPanelProps({
  assignees = [],
  groupedArtists = [],
  onSelectAssignee,
  onSelectArtist,
  buildAssetUrl,
  appendVariant,
  adminProfilePath,
  artistProfilePath,
  assigneeEmptyText = '担当者は設定されていません。',
  artistEmptyText = '参加アーティストがいません。',
  assigneeMetaFallback = '',
  artistNameFallback = 'アーティスト',
  resolveAssigneeKey = resolveGroupAssigneeKey,
  resolveArtistKey = resolveGroupArtistKey,
  assigneeNameStyle,
  artistNameStyle,
  artistRowClassName,
  artistAvatarClassName,
}) {
  return {
    assignees,
    groupedArtists,
    onSelectAssignee,
    onSelectArtist,
    resolveAssigneeKey,
    resolveArtistKey,
    renderAssigneeName: getGroupAssigneeName,
    renderAssigneeMeta: (admin) => getGroupAssigneeMeta(admin, assigneeMetaFallback),
    renderArtistName: (artist) => getGroupArtistName(artist, artistNameFallback),
    resolveAssigneeAvatarStyle: (admin) =>
      getGroupAssigneeAvatarStyle({
        admin,
        buildAssetUrl,
        appendVariant,
        adminProfilePath,
      }),
    resolveAssigneeAvatarFallback: (admin) =>
      getGroupAssigneeAvatarFallback(admin, '管理者'),
    resolveArtistAvatarStyle: (artist) =>
      getGroupArtistAvatarStyle({
        artist,
        buildAssetUrl,
        appendVariant,
        artistProfilePath,
      }),
    resolveArtistAvatarFallback: (artist) =>
      getGroupArtistAvatarFallback(artist, artistNameFallback),
    assigneeEmptyText,
    artistEmptyText,
    assigneeNameStyle,
    artistNameStyle,
    artistRowClassName,
    artistAvatarClassName,
  };
}
